import { FC, useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { RenessansTalkInterval } from '@/entities/renessans-dashboards';
import { formatMMSS } from './utils';

type Props = {
  /** 13 точек по часам 8…20; `avgDuration = null` приводится к `0` для линейного графика. */
  items: RenessansTalkInterval[];
};

const HEIGHT = 64;

type ChartPoint = {
  hour: number;
  avgDuration: number;
};

type SparklineTooltipProps = {
  active?: boolean;
  payload?: { payload?: ChartPoint }[];
};

const SparklineTooltip: FC<SparklineTooltipProps> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-md border bg-popover text-popover-foreground px-2 py-1 text-xs shadow">
      {`${point.hour}:00–${point.hour + 1}:00 · ${formatMMSS(point.avgDuration)}`}
    </div>
  );
};

const GRADIENT_ID = 'talkIntervalsSparklineFill';

/**
 * «Интервалы разговора»: сглаженная area-линия среднего времени
 * разговора по 13 часовым интервалам 8:00…20:00. Часы без звонков
 * (`avgDuration = null`) приводятся к `0`, чтобы линия была непрерывной.
 * Оси скрыты — задача спарклайна показать только форму распределения по дню.
 */
export const TalkIntervalsSparkline: FC<Props> = ({ items }) => {
  const data = useMemo<ChartPoint[]>(
    () =>
      [...items]
        .sort((a, b) => a.hour - b.hour)
        .map((p) => ({
          hour: p.hour,
          avgDuration: p.avgDuration !== null && Number.isFinite(p.avgDuration) ? p.avgDuration : 0,
        })),
    [items],
  );

  const hasValues = useMemo(() => data.some((p) => p.avgDuration > 0), [data]);

  if (!hasValues) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  return (
    <div className="w-full" style={{ height: HEIGHT }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 2, left: 4 }}>
          <defs>
            <linearGradient id={GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.4} />
              <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="hour"
            stroke="var(--color-muted-foreground)"
            tick={{ fontSize: 9 }}
            tickLine={false}
            axisLine={false}
            interval={1}
            tickFormatter={(hour: number) => `${hour}`}
            tickMargin={2}
            height={14}
          />
          <Tooltip content={<SparklineTooltip />} cursor={false} wrapperStyle={{ outline: 'none' }} />
          <Area
            type="monotone"
            dataKey="avgDuration"
            stroke="var(--color-primary)"
            strokeWidth={1.5}
            fill={`url(#${GRADIENT_ID})`}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
